// src/Admin.js - Admin user list (FULL contrast + dropdown fix)
import React, { useMemo, useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
} from '@mui/material';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';

const dropdownMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: '#ffffff',
      color: '#000000',
      '& .MuiMenuItem-root': {
        color: '#000000',
        '&:hover': { bgcolor: '#f5f5f5' },
        '&.Mui-selected': {
          bgcolor: '#e0e0e0',
          '&:hover': { bgcolor: '#d5d5d5' },
        },
      },
    },
  },
};

const formatCreated = (createdAt) => {
  if (createdAt?.seconds) {
    return new Date(createdAt.seconds * 1000).toLocaleDateString();
  }
  return '-';
};

const AdminDashboard = () => {
  const { currentUser, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (authLoading) return;
    if (!currentUser) return navigate('/login', { replace: true });
    if (!isAdmin) return navigate('/', { replace: true });

    const fetchUsers = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'users'));
        const list = snapshot.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            email: data.email || 'Unknown',
            role: data.role || 'user',
            plan: data.plan || 'free',
            subscriptionStatus: data.subscriptionStatus || 'inactive',
            createdAt: data.createdAt,
          };
        });

        list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setUsers(list);
      } catch (err) {
        console.error(err);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [authLoading, currentUser, isAdmin, navigate]);

  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase().trim();
    return users.filter((u) => {
      const matchesSearch =
        !q ||
        u.id.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q);

      const matchesPlan = planFilter === 'all' || u.plan === planFilter;
      const matchesStatus = statusFilter === 'all' || u.subscriptionStatus === statusFilter;

      return matchesSearch && matchesPlan && matchesStatus;
    });
  }, [users, search, planFilter, statusFilter]);

  if (authLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
      <Typography variant="h4" align="center" sx={{ mb: 0.5 }}>
        Admin Dashboard
      </Typography>
      <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
        User management (read-only)
      </Typography>

      {/* FILTER BAR */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Search (email or UID)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            InputLabelProps={{ style: { color: '#000' } }}
            InputProps={{ style: { color: '#000' } }}
          />

          <Select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            sx={{ minWidth: 180, bgcolor: '#fff', color: '#000' }}
            MenuProps={dropdownMenuProps}
          >
            <MenuItem value="all">All plans</MenuItem>
            <MenuItem value="free">Free</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
          </Select>

          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 220, bgcolor: '#fff', color: '#000' }}
            MenuProps={dropdownMenuProps}
          >
            <MenuItem value="all">All subscription statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="past_due">Past Due</MenuItem>
            <MenuItem value="canceled">Canceled</MenuItem>
          </Select>

          <Typography variant="body2" sx={{ ml: 'auto' }} color="text.secondary">
            Showing {filteredUsers.length} of {users.length}
          </Typography>
        </Box>
      </Paper>

      {/* TABLE */}
      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', bgcolor: '#fff' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>UID</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Role</strong></TableCell>
                <TableCell><strong>Plan</strong></TableCell>
                <TableCell><strong>Subscription</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>{u.id}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.role}</TableCell>
                  <TableCell>{u.plan}</TableCell>
                  <TableCell>{u.subscriptionStatus}</TableCell>
                  <TableCell>{formatCreated(u.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default AdminDashboard;



